<template>
  <template v-if="!hideButton">
    <slot name="button" :open-modal="openModal">
      <BaseButton :title="$t('button.open')" @click="opened = true" />
    </slot>
  </template>

  <BaseCenteredModal
    v-model:is-modal-open="opened"
    size="medium"
    :z-index="showTaskModal ? 'z-50' : 'z-100'"
    :title="$t('global.consignments_title')"
  >
    <template #body>
      <div class="relative">
        <div
          v-if="loading"
          class="absolute inset-0 z-10 flex items-center justify-center rounded bg-white/80 dark:bg-slate-700/80"
        >
          <LoadingComponent size="small" :display-label="false" />
        </div>
        <ActivityLogFormFields
          v-model:activity-log="activityLog"
          v-model:selected-dossier="selectedDossier"
          v-model:add-to-internal-note="addToInternalNote"
          v-model:linked-tasks="linkedTasks"
          :filtered-activity-log-types="filteredActivityLogTypes"
          :subject-valid="subjectValid"
          @open-task="openTaskAtIndex"
        />
      </div>
      <TaskDossierModal
        v-model:open="showTaskModal"
        :task-id="selectedTask?.id ?? null"
        default-parent-type="App\Models\Dossier"
        :default-parent-id="activityLog.subject_id"
        :default-dossier-name="selectedDossier?.title ?? null"
        :locked-dossier-id="activityLog.subject_id"
        :edition-mode="!selectedTask?.id"
        :draft-mode="!selectedTask?.id"
        :draft-task="!selectedTask?.id ? selectedTask : null"
      />
    </template>
    <template #footer>
      <div class="flex w-full justify-end gap-2">
        <BaseButton
          :title="$t('button.close')"
          secondary-color
          icon="times"
          :disabled="loading"
          @click="opened = false"
        />
        <BaseButton
          v-if="
            activityLog.subject_type === 'App\\Models\\Dossier' &&
            activityLog.subject_id
          "
          :title="$t('button.add_task')"
          icon="tasks"
          secondary-color
          :disabled="loading"
          @click="addTask"
        />
        <BaseButton
          :title="$t('button.save')"
          :icon="'paper-plane'"
          :disabled="loading"
          @click="saveForm"
        />
      </div>
    </template>
  </BaseCenteredModal>
</template>

<script>
import moment from 'moment'
import BaseCenteredModal from '@c/addf-package/components/modal/BaseCenteredModal.vue'
import BaseButton from '@c/addf-package/components/BaseButton/BaseButton.vue'
import TaskDossierModal from '@c/baseComponents/modal/taskDossier/TaskDossierModal.vue'
import { mapActions, mapGetters, mapMutations } from 'vuex'
import LoadingComponent from '@c/TheLoadingComponent.vue'
import ActivityLogFormFields from './ActivityLogFormFields.vue'
import { CONTACT_EVENTS } from '@u/constants'

export default {
  name: 'ActivityLogModal',
  components: {
    ActivityLogFormFields,
    LoadingComponent,
    TaskDossierModal,
    BaseButton,
    BaseCenteredModal
  },
  props: {
    subjectType: {
      type: String,
      required: false,
      default: null
    },
    subjectId: {
      type: String,
      required: false,
      default: null
    },
    causerType: {
      type: String,
      required: false,
      default: null
    },
    causerId: {
      type: String,
      required: false,
      default: null
    },
    hideButton: {
      type: Boolean,
      default: false
    },
    /**
     * An existing activity to edit (an ActivityBlock's `activity`). Left
     * null, opening the modal starts a new one from the API's blank
     * activity.
     */
    activity: {
      type: Object,
      required: false,
      default: null
    },
    /** Opens the modal from outside, for a `hide-button` owner. */
    open: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update:subjectType', 'update:subjectId', 'update:open'],
  data() {
    return {
      opened: this.open,
      loading: false,
      sending: false,
      addToInternalNote: false,
      selectedDossier: null,
      showTaskModal: false,
      selectedTaskIndex: null,
      linkedTasks: [],
      activityLog: {
        description: null,
        subjectType: this.subjectType ?? null,
        subjectId: this.subjectId ?? null,
        properties: {
          date_time: null,
          notes: ''
        }
      }
    }
  },
  computed: {
    ...mapGetters({
      activityLogTypes: 'metadata/getActivityLogTypes',
      currentDossier: 'dossier/getDossier'
    }),
    filteredActivityLogTypes() {
      return this.activityLogTypes.filter((activityLogType) =>
        CONTACT_EVENTS.includes(activityLogType.id)
      )
    },
    subjectValid() {
      return this.activityLog.subject_type?.length > 0
    },
    isOnDossierPage() {
      return (
        this.currentDossier?.id != null &&
        String(this.$route.params.id) === String(this.currentDossier.id)
      )
    },
    selectedTask() {
      return this.selectedTaskIndex !== null
        ? this.linkedTasks[this.selectedTaskIndex]
        : null
    }
  },
  watch: {
    showTaskModal(newValue) {
      if (!newValue) {
        this.selectedTaskIndex = null
      }
    },
    open(newValue) {
      this.opened = newValue
    },
    opened: {
      handler(newValue) {
        this.$emit('update:open', newValue)
        if (newValue) {
          this.addToInternalNote = false
          this.selectedDossier = null
          this.linkedTasks = []
          if (this.activity) {
            this.loadActivity(this.activity)
          } else {
            this.fetchNewActivityLog()
          }
        }
      },
      // An owner passing `open` already true on mount must still load the
      // activity: `opened` starts from it and never "changes" otherwise.
      immediate: true
    }
  },
  methods: {
    ...mapActions({
      processFetchNewActivityLogAction: 'activityLog/fetchNewActivityLog',
      processStoreActivityLogAction: 'activityLog/storeActivityLog',
      processUpdateActivityLogAction: 'activityLog/updateActivityLog',
      processFetchNewTaskDossierAction: 'taskDossier/fetchNewTaskDossier'
    }),
    ...mapMutations({
      processSetLastActivitySavedAtMutation:
        'activityLog/setLastActivitySavedAt'
    }),
    addTask() {
      this.processFetchNewTaskDossierAction({
        params: {
          defaultParentType: this.activityLog.subject_type,
          defaultParentId: this.activityLog.subject_id
        }
      })
        .then((response) => {
          this.linkedTasks = [
            ...this.linkedTasks,
            { ...response.TaskDossier, _uid: crypto.randomUUID() }
          ]
          this.selectedTaskIndex = this.linkedTasks.length - 1
          this.showTaskModal = true
        })
        .catch(() => {
          this.$h.toastDanger(this.$t('toasts.something_went_wrong'))
        })
    },
    openTaskAtIndex(index) {
      this.selectedTaskIndex = index
      this.showTaskModal = true
    },
    loadActivity(activity) {
      // subject, causer and tasks are read-only relations of the resource,
      // not attributes the update request takes.
      const { subject, causer, tasks, ...attributes } = activity
      this.activityLog = {
        ...attributes,
        created_at: moment(activity.created_at).format('YYYY-MM-DD HH:mm:ss'),
        properties: { ...activity.properties }
      }
      this.selectedDossier =
        activity.subject_type === 'App\\Models\\Dossier' ? subject : null
      this.linkedTasks = [...(tasks ?? [])]
    },
    fetchNewActivityLog() {
      this.loading = true
      this.processFetchNewActivityLogAction()
        .then((response) => {
          this.activityLog = response.activity
          // On écrase la date retournée par le serveur pour capturer l'heure réelle d'encodage côté client
          this.activityLog.created_at = moment().format('YYYY-MM-DD HH:mm:ss')
          if (this.isOnDossierPage) {
            this.activityLog.subject_type = 'App\\Models\\Dossier'
            this.activityLog.subject_id = this.currentDossier.id
            this.selectedDossier = { ...this.currentDossier }
          }
        })
        .catch(() => {
          this.$h.toastDanger(this.$t('toasts.something_went_wrong'))
        })
        .finally(() => {
          this.loading = false
        })
    },
    saveForm() {
      this.loading = true
      const params = {
        ...this.activityLog,
        add_to_internal_note: this.addToInternalNote,
        tasks: this.linkedTasks
      }
      if (this.activityLog.id) {
        this.processUpdateActivityLogAction({
          id: this.activityLog.id,
          params
        })
          .then((response) => {
            if (response.success) {
              this.$h.toastSuccess(this.$t('toasts.updated'))
              this.processSetLastActivitySavedAtMutation()
              this.opened = false
            }
          })
          .catch(() => {
            this.$h.toastDanger(this.$t('toasts.something_went_wrong'))
          })
          .finally(() => {
            this.loading = false
          })
      } else {
        this.processStoreActivityLogAction({ params })
          .then((response) => {
            if (response.success) {
              this.$h.toastSuccess(this.$t('toasts.created'))
              this.processSetLastActivitySavedAtMutation()
              this.opened = false
            }
          })
          .catch(() => {
            this.$h.toastDanger(this.$t('toasts.something_went_wrong'))
          })
          .finally(() => {
            this.loading = false
          })
      }
    },
    openModal() {
      this.opened = true
    }
  }
}
</script>
